import {
  BILLING_DUE_DAYS_OPTIONS,
  type BillingCandidateEntry,
  type BillingDraftDto,
  type BillingDraftItemDto,
  DEFAULT_BILLING_DUE_DAYS,
} from '@fos/shared/contracts/billing';
import { billingPeriodRangeText } from '@fos/shared/labels';
import { todayLocal, toLocalDate } from '@fos/shared/time';
import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { DateInput } from '../../../ui/DateTimeInput';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input, Textarea } from '../../../ui/Input';
import { Mono } from '../../../ui/Mono';
import { Select } from '../../../ui/Select';
import { ErrorState, Skeleton } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { FULLSCREEN_ON_PHONE } from '../../mobile';
import { clockText, dayMonthText, hoursMinutesText, moneyAuto, moneyText } from '../billing-format';

/**
 * Okna zestawienia (Figma 09 Zestawienie): pozycja stała, wysłanie do akceptacji (termin płatności), opłacenie,
 * anulowanie, zmiana okresu, wpisy spoza okresu. Każde po zapisie odświeża dane modułu (`utils.billing`), błąd
 * pokazuje w oknie; fokus wraca na przycisk, który okno otworzył (Dialog + useReturnFocus).
 */

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draft: BillingDraftDto;
}

/** „990,00” / „1 990” / „40.5” → "990.00" | null. */
export function parseAmountInput(raw: string): string | null {
  const text = raw.replace(/[\s ]/g, '').replace(',', '.').replace(/zł$/i, '');
  if (!/^\d{1,10}(\.\d{1,2})?$/.test(text)) return null;
  const [int = '0', frac = ''] = text.split('.');
  return `${int}.${frac.padEnd(2, '0')}`;
}

/** „1”, „2,5” → "1" | "2.5" | null (ilość > 0). */
export function parseQuantityInput(raw: string): string | null {
  const text = raw.replace(/\s/g, '').replace(',', '.');
  if (!/^\d{1,10}(\.\d{1,2})?$/.test(text) || Number(text) <= 0) return null;
  return text;
}

function useFormState(open: boolean) {
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  useEffect(() => {
    if (open) {
      setError(null);
      setFields({});
    }
  }, [open]);
  const fail = (err: unknown) => {
    if (appErrorCode(err) === 'VALIDATION_ERROR') {
      const issues = validationIssues(err);
      if (issues.length > 0) setFields(Object.fromEntries(issues.map((i) => [i.path.split('.')[0] ?? '', i.message])));
    }
    setError(
      appErrorCode(err) === 'CONFLICT' && /zmieniło się/.test(errorMessage(err))
        ? 'Zestawienie zmieniło się w międzyczasie. Zamknij okno, odśwież i spróbuj ponownie.'
        : errorMessage(err),
    );
  };
  return { error, setError, fields, setFields, fail };
}

// ── Pozycja stała ───────────────────────────────────────────────────────────

/** „1.00” → „1”, „990.00” → „990,00” (wartości pola w oknie zmiany pozycji). */
function quantityField(q: string | null): string {
  return (q ?? '1').replace(/\.0+$/, '').replace('.', ',');
}

/**
 * „Dodaj pozycję” / „Zmień pozycję” (`item`): opis, ilość × cena netto (np. „Hosting 12 mies.”). Wymaga „Widzi
 * stawki i kwoty”. Zmiana pozycji — także w wysłanym rozliczeniu z klientem (kwota liczy się od nowa).
 */
export function FixedLineDialog({
  open,
  onOpenChange,
  draft,
  item,
}: DialogProps & { item?: BillingDraftItemDto | null }) {
  const utils = trpc.useUtils();
  const add = trpc.billing.addFixedLine.useMutation();
  const update = trpc.billing.updateLine.useMutation();
  const { error, fields, setFields, fail, setError } = useFormState(open);
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [price, setPrice] = useState('');
  const editing = Boolean(item);
  useEffect(() => {
    if (open) {
      setDescription(item?.description ?? '');
      setQuantity(item ? quantityField(item.quantity) : '1');
      setPrice(item?.unitPrice ? item.unitPrice.replace('.', ',') : '');
    }
  }, [open, item]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const errs: Record<string, string> = {};
    const q = parseQuantityInput(quantity);
    const p = parseAmountInput(price);
    if (!description.trim()) errs.description = 'Opis jest wymagany';
    if (!q) errs.quantity = 'Podaj ilość większą od zera';
    if (!p) errs.unitPrice = 'Podaj kwotę, np. 990,00';
    if (Object.keys(errs).length > 0 || !q || !p) {
      setFields(errs);
      return;
    }
    try {
      const input = {
        draftId: draft.id,
        version: draft.version,
        description: description.trim(),
        quantity: q,
        unitPrice: p,
      };
      if (item) await update.mutateAsync({ ...input, itemId: item.id });
      else await add.mutateAsync(input);
      await utils.billing.invalidate();
      toast.success(item ? 'Zmieniono pozycję' : 'Dodano pozycję');
      onOpenChange(false);
    } catch (err) {
      fail(err);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? 'Zmień pozycję' : 'Dodaj pozycję'}
      className={FULLSCREEN_ON_PHONE}
      description="Pozycja stała, np. hosting albo konfiguracja domeny. Kwota netto = ilość × cena."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="billing-fixed-line" loading={add.isPending || update.isPending}>
            {editing ? 'Zapisz' : 'Dodaj pozycję'}
          </Button>
        </>
      }
    >
      <form id="billing-fixed-line" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {error && <FormMessage>{error}</FormMessage>}
        <Field label="Opis" error={fields.description}>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={300} autoFocus />
        </Field>
        <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-3">
          <Field label="Ilość" error={fields.quantity}>
            <Input value={quantity} onChange={(e) => setQuantity(e.target.value)} inputMode="decimal" mono />
          </Field>
          <Field label={`Cena netto (${draft.currency === 'PLN' ? 'zł' : draft.currency})`} error={fields.unitPrice}>
            <Input
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              inputMode="decimal"
              placeholder="0,00"
              mono
            />
          </Field>
        </div>
      </form>
    </Dialog>
  );
}

// ── Wysłanie do akceptacji ──────────────────────────────────────────────────

/**
 * „Wyślij do wystawienia”: podsumowanie i termin płatności; tworzy akceptację wystawienia faktury (zatwierdza osoba
 * z „Wystawia faktury”), wpisy zostają zablokowane.
 */
export function SubmitDialog({
  open,
  onOpenChange,
  draft,
  settlement = false,
}: DialogProps & { settlement?: boolean }) {
  const access = useAccess();
  const utils = trpc.useUtils();
  const submitMutation = trpc.billing.submitForApproval.useMutation();
  const { error, fail, setError } = useFormState(open);
  const [dueDays, setDueDays] = useState(String(DEFAULT_BILLING_DUE_DAYS));
  useEffect(() => {
    if (open) setDueDays(String(DEFAULT_BILLING_DUE_DAYS));
  }, [open]);
  const count = draft.items.length;
  // „Wystaw fakturę” (jedno okno potwierdzenia) dla osoby z „Wystawia faktury”; inni wysyłają do akceptacji.
  // Tylko etykieta: backend rozstrzyga (zestawienie wraca z akceptacją już zatwierdzoną albo czekającą).
  // rozliczenie z klientem: osoba z prawem używa „Oznacz jako wysłane klientowi” (MarkSentDialog), tu — do akceptacji
  const canIssueNow =
    !settlement && access.can('approvals', 'edit') && access.flag('issuesInvoices') && access.flag('seesRates');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const saved = await submitMutation.mutateAsync({
        id: draft.id,
        version: draft.version,
        dueDays: Number(dueDays),
      });
      await utils.billing.invalidate();
      const issuing = saved.approval?.status === 'approved' || saved.approval?.status === 'executing';
      toast.success(issuing ? (settlement ? 'Oznaczamy jako wysłane' : 'Wystawiamy fakturę') : 'Wysłano do akceptacji');
      onOpenChange(false);
    } catch (err) {
      fail(err);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={canIssueNow ? 'Wystawić fakturę?' : settlement ? 'Wyślij do akceptacji' : 'Wyślij do wystawienia'}
      description={
        settlement
          ? 'Rozliczenie trafi do Akceptacji. Po zatwierdzeniu dostanie status „Wysłane do klienta”, a te wpisy zostaną zablokowane.'
          : canIssueNow
            ? 'System fakturowy wystawi fakturę od razu, a te wpisy zostaną zablokowane.'
            : 'Zestawienie trafi do Akceptacji. Po zatwierdzeniu system fakturowy wystawi fakturę, a te wpisy zostaną zablokowane.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="billing-submit" loading={submitMutation.isPending}>
            {canIssueNow ? 'Wystaw fakturę' : 'Wyślij do akceptacji'}
          </Button>
        </>
      }
    >
      <form id="billing-submit" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {error && <FormMessage>{error}</FormMessage>}
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-2 rounded-lg bg-surface px-4 py-3 text-body-s">
          <dt className="text-fg-muted">Klient</dt>
          <dd className="truncate text-right text-fg">{draft.client.name}</dd>
          <dt className="text-fg-muted">Okres</dt>
          <dd className="text-right">
            <Mono>{billingPeriodRangeText(draft.periodFrom, draft.periodTo)}</Mono>
          </dd>
          <dt className="text-fg-muted">Pozycje</dt>
          <dd className="text-right text-fg">
            {count} {plural(count, ['pozycja', 'pozycje', 'pozycji'])} ·{' '}
            <Mono>{hoursMinutesText(draft.totalSeconds)}</Mono>
          </dd>
          {access.flag('seesRates') && draft.totalNet !== undefined && (
            <>
              <dt className="text-fg-muted">Razem netto</dt>
              <dd className="text-right font-semibold text-fg">
                <Mono>{moneyText(draft.totalNet, draft.currency)}</Mono>
              </dd>
            </>
          )}
        </dl>
        {draft.entriesWithoutRate > 0 && (
          <FormMessage>
            {draft.entriesWithoutRate}{' '}
            {plural(draft.entriesWithoutRate, ['wpis nie ma', 'wpisy nie mają', 'wpisów nie ma'])} stawki —{' '}
            {settlement ? 'kwota 0 zł.' : 'nie trafi na fakturę.'}
          </FormMessage>
        )}
        <Field label="Termin płatności">
          <Select
            value={dueDays}
            onValueChange={setDueDays}
            options={BILLING_DUE_DAYS_OPTIONS.map((d) => ({ value: String(d), label: `${d} dni` }))}
          />
        </Field>
      </form>
    </Dialog>
  );
}

// ── Opłacenie ───────────────────────────────────────────────────────────────

/** „Oznacz jako opłacone”: data wpływu (domyślnie dziś). */
export function MarkPaidDialog({ open, onOpenChange, draft }: DialogProps) {
  const utils = trpc.useUtils();
  const mark = trpc.billing.markPaid.useMutation();
  const { error, fields, setFields, fail, setError } = useFormState(open);
  const today = todayLocal();
  const [paidOn, setPaidOn] = useState(today);
  useEffect(() => {
    if (open) setPaidOn(today);
  }, [open, today]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!paidOn) {
      setFields({ paidOn: 'Podaj datę opłacenia' });
      return;
    }
    try {
      await mark.mutateAsync({ id: draft.id, version: draft.version, paidOn });
      await utils.billing.invalidate();
      toast.success('Oznaczono jako opłacone');
      onOpenChange(false);
    } catch (err) {
      fail(err);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Oznacz jako opłacone"
      description={
        draft.invoice?.number
          ? `${draft.invoice.provider === 'manual' ? 'Numer faktury' : 'Faktura'} ${draft.invoice.number}`
          : undefined
      }
      width="s"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="billing-paid" loading={mark.isPending}>
            Oznacz jako opłacone
          </Button>
        </>
      }
    >
      <form id="billing-paid" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {error && <FormMessage>{error}</FormMessage>}
        <Field label="Data opłacenia" error={fields.paidOn}>
          <DateInput value={paidOn} onValueChange={setPaidOn} max={today} />
        </Field>
      </form>
    </Dialog>
  );
}

// ── Oznacz jako wystawione ──────────────────────────────────────────────────

/**
 * „Oznacz jako wystawione” (zestawienie z nieznanym wynikiem wystawienia, faktura jednak powstała): numer i data
 * wystawienia przepisane z systemu faktur, opcjonalnie identyfikator faktury u dostawcy (wtedy opłacenie przyjdzie
 * z synchronizacji). Tylko osoba z „Wystawia faktury”; termin płatności liczy się z dni z akceptacji.
 */
export function MarkIssuedDialog({ open, onOpenChange, draft }: DialogProps) {
  const utils = trpc.useUtils();
  const mark = trpc.billing.markIssued.useMutation();
  const { error, fields, setFields, fail, setError } = useFormState(open);
  const today = todayLocal();
  const [number, setNumber] = useState('');
  const [issueDate, setIssueDate] = useState(today);
  const [externalId, setExternalId] = useState('');
  useEffect(() => {
    if (open) {
      setNumber('');
      setIssueDate(today);
      setExternalId('');
    }
  }, [open, today]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const next: Record<string, string> = {};
    if (!number.trim()) next.number = 'Podaj numer faktury z systemu faktur';
    if (!issueDate) next.issueDate = 'Podaj datę wystawienia';
    setFields(next);
    if (Object.keys(next).length > 0) return;
    try {
      await mark.mutateAsync({
        id: draft.id,
        version: draft.version,
        number: number.trim(),
        issueDate,
        ...(externalId.trim() ? { externalId: externalId.trim() } : {}),
      });
      await utils.billing.invalidate();
      toast.success(`Oznaczono jako wystawione: ${number.trim()}`);
      onOpenChange(false);
    } catch (err) {
      fail(err);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Oznacz jako wystawione"
      description="Faktura powstała w systemie faktur mimo błędu. Przepisz jej numer i datę — drugiej faktury nie wystawimy."
      width="s"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="billing-mark-issued" loading={mark.isPending}>
            Oznacz jako wystawione
          </Button>
        </>
      }
    >
      <form id="billing-mark-issued" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {error && <FormMessage>{error}</FormMessage>}
        <Field label="Numer faktury" error={fields.number}>
          <Input mono value={number} onChange={(e) => setNumber(e.target.value)} placeholder="FV/2026/10/017" />
        </Field>
        <Field label="Data wystawienia" error={fields.issueDate}>
          <DateInput value={issueDate} onValueChange={setIssueDate} max={today} />
        </Field>
        <Field
          label="Identyfikator w systemie faktur (opcjonalnie)"
          hint="Z adresu faktury, np. 4242. Dzięki niemu opłacenie przyjdzie samo."
          error={fields.externalId}
        >
          <Input mono value={externalId} onChange={(e) => setExternalId(e.target.value)} />
        </Field>
      </form>
    </Dialog>
  );
}

// ── Anulowanie ──────────────────────────────────────────────────────────────

/**
 * Anulowanie zestawienia: wpisy wracają do niezafakturowanych (wystawionego nie anulujemy). Rozliczenie z klientem —
 * także wysłane („Anuluj rozliczenie”).
 */
export function CancelDraftDialog({
  open,
  onOpenChange,
  draft,
  settlement = false,
}: DialogProps & { settlement?: boolean }) {
  const utils = trpc.useUtils();
  const cancel = trpc.billing.cancel.useMutation();
  const { error, fail, setError } = useFormState(open);
  const confirm = async () => {
    setError(null);
    try {
      await cancel.mutateAsync({ id: draft.id, version: draft.version });
      await utils.billing.invalidate();
      toast.success(settlement ? 'Anulowano rozliczenie' : 'Anulowano zestawienie');
      onOpenChange(false);
    } catch (err) {
      fail(err);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={settlement ? `Anulować rozliczenie ${draft.label}?` : `Anulować zestawienie ${draft.label}?`}
      description={
        settlement
          ? 'Rozliczenie zostanie w historii jako anulowane. Wpisy wrócą do nierozliczonych i będzie można rozliczyć je w innym rozliczeniu.'
          : 'Wpisy wrócą do niezafakturowanych i będzie można rozliczyć je w innym zestawieniu.'
      }
      width="s"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Wróć
          </Button>
          <Button variant="primary" onClick={() => void confirm()} loading={cancel.isPending}>
            {settlement ? 'Anuluj rozliczenie' : 'Anuluj zestawienie'}
          </Button>
        </>
      }
    >
      {error && <FormMessage>{error}</FormMessage>}
    </Dialog>
  );
}

// ── Rozliczenia z klientem ──────────────────────────────────────────────────

/**
 * „Oznacz jako wysłane klientowi” (tryb rozliczeń, osoba z „Wystawia faktury”): data wysłania (domyślnie dziś; data
 * rozliczenia w Statystykach), opcjonalny numer faktury z programu księgowego i termin płatności. Bez akceptacji.
 */
export function MarkSentDialog({ open, onOpenChange, draft }: DialogProps) {
  const utils = trpc.useUtils();
  const mark = trpc.billing.markSent.useMutation();
  const { error, fields, setFields, fail, setError } = useFormState(open);
  const today = todayLocal();
  const [sentOn, setSentOn] = useState(today);
  const [number, setNumber] = useState('');
  const [dueDays, setDueDays] = useState(String(DEFAULT_BILLING_DUE_DAYS));
  useEffect(() => {
    if (open) {
      setSentOn(today);
      setNumber(draft.invoiceNumber ?? '');
      setDueDays(String(DEFAULT_BILLING_DUE_DAYS));
    }
  }, [open, today, draft.invoiceNumber]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!sentOn) {
      setFields({ sentOn: 'Podaj datę wysłania' });
      return;
    }
    try {
      await mark.mutateAsync({
        id: draft.id,
        version: draft.version,
        sentOn,
        invoiceNumber: number.trim() || null,
        dueDays: Number(dueDays),
      });
      await utils.billing.invalidate();
      toast.success('Oznaczono jako wysłane klientowi');
      onOpenChange(false);
    } catch (err) {
      fail(err);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Oznacz jako wysłane klientowi"
      description="Rozliczenie dostanie status „Wysłane do klienta”. Do opłacenia nadal możesz je zmieniać."
      width="s"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="billing-mark-sent" loading={mark.isPending}>
            Oznacz jako wysłane klientowi
          </Button>
        </>
      }
    >
      <form id="billing-mark-sent" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {error && <FormMessage>{error}</FormMessage>}
        <Field label="Data wysłania" error={fields.sentOn}>
          <DateInput value={sentOn} onValueChange={setSentOn} max={today} />
        </Field>
        <Field
          label="Numer faktury (opcjonalnie)"
          hint="Np. z programu księgowego. Niczego nie blokuje."
          error={fields.invoiceNumber}
        >
          <Input mono value={number} onChange={(e) => setNumber(e.target.value)} maxLength={60} />
        </Field>
        <Field label="Termin płatności">
          <Select
            value={dueDays}
            onValueChange={setDueDays}
            options={BILLING_DUE_DAYS_OPTIONS.map((d) => ({ value: String(d), label: `${d} dni` }))}
          />
        </Field>
      </form>
    </Dialog>
  );
}

/**
 * Szczegóły rozliczenia z klientem: numer faktury (opcjonalny — także w opłaconym), data wysłania (wysłane) i opis
 * (szkic, wysłane). Termin płatności przesuwa się razem z datą wysłania.
 */
export function SettlementDetailsDialog({ open, onOpenChange, draft }: DialogProps) {
  const utils = trpc.useUtils();
  const update = trpc.billing.update.useMutation();
  const { error, fields, fail, setError } = useFormState(open);
  const today = todayLocal();
  const sent = draft.status === 'issued' && !draft.paidAt;
  const paid = draft.status === 'issued' && Boolean(draft.paidAt);
  const currentSentOn = draft.invoice?.issuedAt ? toLocalDate(new Date(draft.invoice.issuedAt)) : '';
  const [number, setNumber] = useState('');
  const [sentOn, setSentOn] = useState('');
  const [notes, setNotes] = useState('');
  useEffect(() => {
    if (open) {
      setNumber(draft.invoiceNumber ?? '');
      setSentOn(currentSentOn);
      setNotes(draft.notes ?? '');
    }
  }, [open, draft.invoiceNumber, draft.notes, currentSentOn]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const nextNumber = number.trim() || null;
    const nextNotes = notes.trim() || null;
    try {
      await update.mutateAsync({
        id: draft.id,
        version: draft.version,
        ...(nextNumber !== (draft.invoiceNumber ?? null) ? { invoiceNumber: nextNumber } : {}),
        ...(sent && sentOn && sentOn !== currentSentOn ? { sentOn } : {}),
        ...(!paid && nextNotes !== (draft.notes ?? null) ? { notes: nextNotes } : {}),
      });
      await utils.billing.invalidate();
      toast.success('Zapisano rozliczenie');
      onOpenChange(false);
    } catch (err) {
      fail(err);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Szczegóły rozliczenia"
      description={paid ? 'Rozliczenie jest opłacone — możesz dopisać numer faktury.' : undefined}
      width="s"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="billing-settlement-details" loading={update.isPending}>
            Zapisz
          </Button>
        </>
      }
    >
      <form id="billing-settlement-details" onSubmit={submit} className="flex flex-col gap-4" noValidate>
        {error && <FormMessage>{error}</FormMessage>}
        <Field
          label="Numer faktury (opcjonalnie)"
          hint="Np. z programu księgowego. Niczego nie blokuje."
          error={fields.invoiceNumber}
        >
          <Input mono value={number} onChange={(e) => setNumber(e.target.value)} maxLength={60} autoFocus />
        </Field>
        {sent && (
          <Field label="Data wysłania" hint="Data rozliczenia w Statystykach." error={fields.sentOn}>
            <DateInput value={sentOn} onValueChange={setSentOn} max={today} />
          </Field>
        )}
        {!paid && (
          <Field label="Opis" error={fields.notes}>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000} rows={3} />
          </Field>
        )}
      </form>
    </Dialog>
  );
}

/** „Otwórz do edycji” opłaconego rozliczenia: wraca do „Wysłane do klienta”, data opłacenia znika (potwierdzenie). */
export function ReopenPaidDialog({ open, onOpenChange, draft }: DialogProps) {
  const utils = trpc.useUtils();
  const reopen = trpc.billing.reopenPaid.useMutation();
  const { error, fail, setError } = useFormState(open);
  const confirm = async () => {
    setError(null);
    try {
      await reopen.mutateAsync({ id: draft.id, version: draft.version });
      await utils.billing.invalidate();
      toast.success('Rozliczenie otwarte do edycji');
      onOpenChange(false);
    } catch (err) {
      fail(err);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Otworzyć rozliczenie do edycji?"
      description="Data opłacenia zostanie usunięta, a rozliczenie wróci do „Wysłane do klienta”. Po zmianach oznacz je ponownie jako opłacone."
      width="s"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Wróć
          </Button>
          <Button variant="primary" onClick={() => void confirm()} loading={reopen.isPending}>
            Otwórz do edycji
          </Button>
        </>
      }
    >
      {error && <FormMessage>{error}</FormMessage>}
    </Dialog>
  );
}

// ── Okres ───────────────────────────────────────────────────────────────────

/** Zmiana okresu zestawienia roboczego (okres musi obejmować jego wpisy). */
export function PeriodDialog({ open, onOpenChange, draft }: DialogProps) {
  const utils = trpc.useUtils();
  const update = trpc.billing.update.useMutation();
  const { error, fields, setFields, fail, setError } = useFormState(open);
  const [from, setFrom] = useState(draft.periodFrom);
  const [to, setTo] = useState(draft.periodTo);
  useEffect(() => {
    if (open) {
      setFrom(draft.periodFrom);
      setTo(draft.periodTo);
    }
  }, [open, draft.periodFrom, draft.periodTo]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!from || !to || to < from) {
      setFields({ periodTo: 'Koniec okresu przed początkiem' });
      return;
    }
    try {
      await update.mutateAsync({ id: draft.id, version: draft.version, periodFrom: from, periodTo: to });
      await utils.billing.invalidate();
      toast.success('Zmieniono okres');
      onOpenChange(false);
    } catch (err) {
      fail(err);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Zmień okres"
      width="s"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" form="billing-period" loading={update.isPending}>
            Zapisz
          </Button>
        </>
      }
    >
      <form id="billing-period" onSubmit={submit} className="grid grid-cols-2 gap-3" noValidate>
        {error && (
          <div className="col-span-2">
            <FormMessage>{error}</FormMessage>
          </div>
        )}
        <Field label="Od" error={fields.periodFrom}>
          <DateInput value={from} onValueChange={setFrom} />
        </Field>
        <Field label="Do" error={fields.periodTo}>
          <DateInput value={to} onValueChange={setTo} />
        </Field>
      </form>
    </Dialog>
  );
}

// ── Wpisy spoza okresu ──────────────────────────────────────────────────────

/**
 * „Dodaj wpisy”: niezafakturowane wpisy klienta spoza okresu zestawienia (np. zapomniany wpis z poprzedniego
 * miesiąca). Dodanie poszerza okres zestawienia.
 */
export function AddEntriesDialog({ open, onOpenChange, draft }: DialogProps) {
  const access = useAccess();
  const seesRates = access.flag('seesRates');
  const utils = trpc.useUtils();
  const add = trpc.billing.addEntries.useMutation();
  const { error, fail, setError } = useFormState(open);
  const query = trpc.billing.candidates.useQuery({ clientId: draft.client.id }, { enabled: open });
  const entries = useMemo<BillingCandidateEntry[]>(
    () =>
      (query.data?.groups ?? [])
        .flatMap((g) => g.entries)
        .filter((e) => e.date < draft.periodFrom || e.date > draft.periodTo)
        .sort((a, b) => (a.date < b.date ? 1 : -1)),
    [query.data, draft.periodFrom, draft.periodTo],
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (open) setSelected(new Set());
  }, [open]);

  const submit = async () => {
    setError(null);
    try {
      await add.mutateAsync({ draftId: draft.id, version: draft.version, timeEntryIds: [...selected] });
      await utils.billing.invalidate();
      toast.success(`Dodano ${selected.size} ${plural(selected.size, ['wpis', 'wpisy', 'wpisów'])}`);
      onOpenChange(false);
    } catch (err) {
      fail(err);
    }
  };

  let body: ReactNode;
  if (query.isPending) {
    body = (
      <div role="status" aria-label="Wczytywanie wpisów" className="flex flex-col gap-2">
        <Skeleton className="h-9" />
        <Skeleton className="h-9" />
      </div>
    );
  } else if (query.isError) {
    body = <ErrorState compact onRetry={() => void query.refetch()} retrying={query.isFetching} />;
  } else if (entries.length === 0) {
    body = (
      <p className="rounded-lg bg-surface px-4 py-3 text-body-s text-fg-secondary">
        Klient nie ma innych niezafakturowanych wpisów.
      </p>
    );
  } else {
    body = (
      <ul className="max-h-[320px] overflow-y-auto" aria-label="Wpisy spoza okresu">
        {entries.map((e) => (
          <li key={e.id} className="flex min-h-10 items-center gap-3 border-b border-line py-1.5">
            <Checkbox
              checked={selected.has(e.id)}
              onCheckedChange={(on) =>
                setSelected((prev) => {
                  const next = new Set(prev);
                  if (on) next.add(e.id);
                  else next.delete(e.id);
                  return next;
                })
              }
              label={
                <span className="flex min-w-0 items-baseline gap-2">
                  <Mono className="text-caption text-fg-muted">{dayMonthText(e.date)}</Mono>
                  <span className="min-w-0 truncate text-fg">{e.description || 'Praca'}</span>
                </span>
              }
              className="min-w-0 flex-1"
            />
            <Mono className="w-12 text-right text-caption text-fg">{clockText(e.durationSeconds)}</Mono>
            {seesRates && e.amount !== undefined && (
              <Mono className="hidden w-20 text-right text-caption sm:inline">
                {moneyAuto(e.amount, draft.currency)}
              </Mono>
            )}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Dodaj wpisy"
      className={FULLSCREEN_ON_PHONE}
      description="Niezafakturowane wpisy klienta spoza okresu zestawienia. Dodanie poszerzy okres."
      width="l"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={selected.size === 0}
            loading={add.isPending}
          >
            {selected.size > 0 ? `Dodaj (${selected.size})` : 'Dodaj'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error && <FormMessage>{error}</FormMessage>}
        {body}
      </div>
    </Dialog>
  );
}
